import React, { useState, useRef } from 'react';
import { Button, FormControl, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { svgIcons } from '../../constants/svgIcons';
import FilterDropdown from '../manageInventory/FilterDropdown';
import Toggle from 'react-toggle';

interface IEquipmentSearchBarProps {
    onBackClick?: () => void;
    onSearchChange?: (value: string) => void;
    categoryOptions?: { value: string; label: string }[];
    selectedCategory?: string;
    onCategoryChange?: (category: string) => void;
    statusOptions?: { value: string; label: string }[];
    selectedStatus?: string;
    onStatusChange?: (status: string) => void;
    buildingOptions?: { value: string; label: string }[];
    selectedBuilding?: string;
    onBuildingChange?: (building: string) => void;
    floorOptions?: { value: string; label: string }[];
    selectedFloor?: string;
    onFloorChange?: (floor: string) => void;
    zoneOptions?: { value: string; label: string }[];
    selectedZone?: string;
    onZoneChange?: (zone: string) => void;
    onClearFilters?: () => void;
    loading?: boolean;
    hideDecommissionedAssets?: boolean;
    onHideAssetsToggle?: (value: boolean) => void;
}

const EquipmentSearchBar: React.FC<IEquipmentSearchBarProps> = ({
    onBackClick,
    onSearchChange,
    categoryOptions = [{ value: 'All', label: 'Category: All' }],
    selectedCategory = 'All',
    onCategoryChange,
    statusOptions = [{ value: 'All', label: 'Status: All' }],
    selectedStatus = 'All',
    onStatusChange,
    buildingOptions = [{ value: 'All', label: 'Bldg: All' }],
    selectedBuilding = 'All',
    onBuildingChange,
    floorOptions = [{ value: 'All', label: 'Floor: All' }],
    selectedFloor = 'All',
    onFloorChange,
    zoneOptions = [{ value: 'All', label: 'Zone: All' }],
    selectedZone = 'All',
    onZoneChange,
    onClearFilters,
    loading = false,
    hideDecommissionedAssets = false,
    onHideAssetsToggle
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [dropdownAlignRight, setDropdownAlignRight] = useState(false);
    const actionsDropdownRef = useRef<HTMLDivElement>(null);

    const hasActiveFilters =
        searchValue !== '' ||
        selectedCategory !== 'All' ||
        selectedStatus !== 'All' ||
        selectedBuilding !== 'All' ||
        selectedFloor !== 'All' ||
        selectedZone !== 'All';

    const handleClearFilters = () => {
        setSearchValue('');
        onClearFilters?.();
    };

    const handleSearchChange = (e: any) => {
        const value = e.target.value;
        setSearchValue(value);
        onSearchChange?.(value);
    };

    const handleFilterChange = (filterType: string, value: string) => {
        switch (filterType) {
            case 'category':
                onCategoryChange?.(value);
                break;
            case 'status':
                onStatusChange?.(value);
                break;
            case 'building':
                onBuildingChange?.(value);
                break;
            case 'floor':
                onFloorChange?.(value);
                break;
            case 'zone':
                onZoneChange?.(value);
                break;
        }
    };

    const handleHideAssetsToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        onHideAssetsToggle?.(e.target.checked);
    };

    const handleDropdownToggle = (isOpen: boolean) => {
        if (isOpen && actionsDropdownRef.current) {
            const button = actionsDropdownRef.current.querySelector(
                '.actions-button'
            );
            if (button) {
                const rect = button.getBoundingClientRect();
                const dropdownWidth = 320;
                const shouldAlignRight =
                    rect.left + dropdownWidth > window.innerWidth;
                setDropdownAlignRight(shouldAlignRight);
            }
        }
    };

    return (
        <div className="inventory-search-form">
            <div className="search-bar-container">
                {/* Back Button */}
                <Button className="back-button" onClick={onBackClick}>
                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                </Button>

                {/* Search Input */}
                <div className="search-input-container">
                    <span className="search-icon">{svgIcons.search}</span>
                    <FormControl
                        type="text"
                        placeholder="Search for Product/Asset.."
                        value={searchValue}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Filter Dropdowns */}
                <FilterDropdown
                    value={selectedCategory}
                    options={categoryOptions}
                    onChange={value => handleFilterChange('category', value)}
                    placeholder="Category: All"
                    defaultValue="All"
                    disabled={loading}
                />

                <FilterDropdown
                    value={selectedStatus}
                    options={statusOptions}
                    onChange={value => handleFilterChange('status', value)}
                    placeholder="Status: All"
                    defaultValue="All"
                    disabled={loading}
                />

                <FilterDropdown
                    value={selectedBuilding}
                    options={buildingOptions}
                    onChange={value => handleFilterChange('building', value)}
                    placeholder="Bldg: All"
                    defaultValue="All"
                    disabled={loading}
                />

                <FilterDropdown
                    value={selectedFloor}
                    options={floorOptions}
                    onChange={value => handleFilterChange('floor', value)}
                    placeholder="Floor: All"
                    defaultValue="All"
                    disabled={
                        loading ||
                        selectedBuilding === 'All' ||
                        (floorOptions.length === 1 &&
                            floorOptions[0].label.includes('No options'))
                    }
                />

                <FilterDropdown
                    value={selectedZone}
                    options={zoneOptions}
                    onChange={value => handleFilterChange('zone', value)}
                    placeholder="Zone: All"
                    defaultValue="All"
                    disabled={
                        loading ||
                        selectedBuilding === 'All' ||
                        selectedFloor === 'All' ||
                        (zoneOptions.length === 1 &&
                            zoneOptions[0].label.includes('No options'))
                    }
                />

                {/* Clear Button - only show when filters are active */}
                {hasActiveFilters && (
                    <Button
                        className="clear-filters-button"
                        onClick={handleClearFilters}
                        disabled={loading}
                    >
                        <span>Clear</span>
                        <FontAwesomeIcon icon={['far', 'times']} />
                    </Button>
                )}

                {/* Actions Dropdown */}
                <div ref={actionsDropdownRef}>
                    <Dropdown
                        onToggle={handleDropdownToggle}
                        className="filter-dropdown actions-dropdown"
                        id="actions-dropdown"
                    >
                        <Dropdown.Toggle noCaret className="actions-button">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="12"
                                viewBox="0 0 14 12"
                                fill="none"
                            >
                                <path
                                    d="M1.53809 4.66699C2.38774 4.66699 3.07712 5.35543 3.07715 6.20508C3.07715 7.05475 2.38775 7.74316 1.53809 7.74316C0.688515 7.74305 0 7.05468 0 6.20508C2.71552e-05 5.3555 0.688532 4.66711 1.53809 4.66699ZM6.66699 4.66699C7.51642 4.66725 8.20505 5.35559 8.20508 6.20508C8.20508 7.05459 7.51644 7.74291 6.66699 7.74316C5.81732 7.74316 5.12793 7.05475 5.12793 6.20508C5.12796 5.35543 5.81734 4.66699 6.66699 4.66699ZM11.7949 4.66699C12.6445 4.6671 13.333 5.3555 13.333 6.20508C13.333 7.05468 12.6445 7.74306 11.7949 7.74316C10.9453 7.74316 10.2559 7.05475 10.2559 6.20508C10.2559 5.35543 10.9453 4.66699 11.7949 4.66699Z"
                                    fill="white"
                                />
                            </svg>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                            className={`filter-dropdown-menu actions-dropdown-menu ${
                                dropdownAlignRight ? 'dropdown-right' : ''
                            }`}
                        >
                            {/* Toggle Switch Section */}
                            <div
                                className="actions-toggle-section"
                                onClick={e => e.stopPropagation()}
                            >
                                <span className="actions-toggle-label">
                                    Hide Virtual/Decommissioned Assets
                                </span>
                                <Toggle
                                    checked={hideDecommissionedAssets}
                                    onChange={handleHideAssetsToggle}
                                    icons={false}
                                />
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
        </div>
    );
};

export default EquipmentSearchBar;
